import { type JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, ButtonGroup, Tooltip } from '@heroui/react';
import { ArrowLeft, Clock, GitBranch, Pencil, SortAsc } from 'lucide-react';

import { type PageChrome, useSetPageChrome } from '~/contexts/PageChromeContext';
import {
  useProduct,
  useProductProjectsLane,
  useProductProjectStatusCounts,
} from '~/hooks/useProducts';
import { useProjectOperations } from '~/hooks/useProjectOperations';
import { useUIStore } from '~/stores/uiStore';
import { pluralize } from '~/utils/pluralize';
import { FILTER_CHIPS, STATUS_DISPLAY, type StatusChip } from '~/utils/status';

import { Board } from '~/components/board/Board';
import { BoardColumn } from '~/components/board/BoardColumn';
import { FilterBar } from '~/components/layout/FilterBar';
import { MAIN_CONTENT_WIDTH } from '~/components/layout/mainContent';

import type { Product, ProjectBoardSort, ProjectProps, ProjectWithTasks } from '~/types';

import { ProjectCard } from '../Projects/ProjectCard';

import { EditProductModal } from './modals/EditProductModal';
import { LinkReposModal } from './modals/LinkReposModal';

/**
 * Per-column initial-load placeholder, shown inside a single `BoardColumn`
 * while its own lane query is still loading — independent of the other two
 * columns and of the product-level `ProductViewSkeleton` below, since each
 * lane now resolves on its own schedule (FE-003).
 */
function ColumnSkeleton(): JSX.Element {
  return (
    <div aria-hidden="true" className="animate-pulse space-y-2 px-1 py-2">
      <div className="h-16 rounded bg-surface-elevated" />
      <div className="h-16 rounded bg-surface-elevated" />
    </div>
  );
}

function ProductViewSkeleton(): JSX.Element {
  return (
    <div
      aria-busy="true"
      aria-label="Loading product"
      className={`flex h-full flex-col gap-4 overflow-hidden p-6 animate-pulse ${MAIN_CONTENT_WIDTH}`}
      role="status"
    >
      <div className="h-4 w-32 rounded bg-surface-elevated" />
      <div className="flex items-center gap-3">
        <div className="h-6 w-6 rounded bg-surface-elevated" />
        <div>
          <div className="mb-1 h-6 w-48 rounded bg-surface-elevated" />
          <div className="h-4 w-64 rounded bg-surface-elevated" />
        </div>
      </div>
      <div className="h-9 w-full max-w-md rounded bg-surface-elevated" />
      <div className="flex flex-1 flex-col gap-6 overflow-hidden lg:flex-row">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex-1 rounded-lg border border-default p-4">
            <div className="mb-3 h-4 w-20 rounded bg-surface-elevated" />
            <div className="space-y-2">
              <div className="h-16 rounded bg-surface-elevated" />
              <div className="h-16 rounded bg-surface-elevated" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Breadcrumb trail for the product Kanban board: `Products` always, plus the
 * product name (leaf, no `to`) once loaded. Pure — no React, no I/O — so it
 * can be unit-tested directly without rendering the page.
 *
 * Pattern: docs/design-patterns/common/code-readability.md — small, named helper
 */
export function productBreadcrumbs(
  product: Product | undefined,
): NonNullable<PageChrome['breadcrumbs']> {
  const crumbs: NonNullable<PageChrome['breadcrumbs']> = [{ label: 'Products', to: '/products' }];
  if (product) {
    crumbs.push({ label: product.name });
  }
  return crumbs;
}

/**
 * Full-page product Kanban board: breadcrumb, product header (Edit / Link
 * Repository actions), a search+sort FilterBar, and a 3-column Board of
 * ProjectCards, each column independently paginated.
 *
 * Container — owns data fetching (useProduct, three useProductProjectsLane
 * instances — one per status group — and one useProductProjectStatusCounts),
 * search/sort state, and modal open state; delegates card interactions to
 * useProjectOperations and rendering to shared Board/BoardColumn/ProjectCard/
 * FilterBar components.
 *
 * FE-003: replaces the single useProductProjects fetch + client-side
 * useProjectFiltering/groupByColumn pipeline. Each column is now its own
 * cursor-paginated, server-sorted, server-searched useInfiniteQuery lane —
 * status grouping, sorting, and search matching all happen server-side, so
 * there is no client-side filtering pipeline left to own.
 * Pattern: docs/design-patterns/frontend/component-patterns.md
 * Pattern: docs/design-patterns/frontend/state-management.md — sort/search
 *   are query-key inputs to the lane queries, not a client-side filter step
 */
export function ProductView(): JSX.Element {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const headingRef = useRef<HTMLHeadingElement>(null);

  const { data: product, isLoading: productLoading } = useProduct(productId ?? '');

  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState<ProjectBoardSort>('newest');

  // Debounced so a server round-trip isn't fired on every keystroke — the old
  // client-side filter was free to run per-keystroke, but search is now a
  // query-key input each lane sends to the backend. searchQuery (undebounced)
  // still drives the controlled input, so typing itself stays instant.
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearchQuery(searchQuery), 300);
    return (): void => clearTimeout(handle);
  }, [searchQuery]);
  const q = debouncedSearchQuery.trim() || undefined;

  const todoLane = useProductProjectsLane(productId ?? '', 'TODO', sortOption, q);
  const inProgressLane = useProductProjectsLane(productId ?? '', 'IN_PROGRESS', sortOption, q);
  const doneLane = useProductProjectsLane(productId ?? '', 'DONE', sortOption, q);
  // Deliberately NOT passed `q` — counts stay unfiltered so the header
  // subtitle and the FilterBar's "of M" denominator never shrink while
  // searching (only the lanes' rendered items, and the FilterBar's "shown"
  // count derived from them below, are search-scoped). See toProjectProps'
  // doc and the "shown"/noSearchMatches derivation below for the rest of
  // this split.
  const counts = useProductProjectStatusCounts(productId ?? '', undefined);

  const totalCount =
    (counts.data?.TODO ?? 0) + (counts.data?.IN_PROGRESS ?? 0) + (counts.data?.DONE ?? 0);

  const chrome = useMemo<PageChrome>(
    () => ({
      title: product?.name ?? 'Product',
      breadcrumbs: productBreadcrumbs(product),
      subtitle: pluralize(totalCount, 'project'),
    }),
    [product, totalCount],
  );
  useSetPageChrome(chrome);

  const { editingProjectIds } = useUIStore();
  const { handleGenerateTasksFromPRD, handleProjectDelete, handleProjectUpdate } =
    useProjectOperations();

  const [editOpen, setEditOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);

  // Move focus to the product heading on route change (a new productId), per
  // frontend-accessibility's SPA route-change focus rule.
  useEffect(() => {
    if (!productLoading && product) {
      headingRef.current?.focus();
    }
  }, [productLoading, product]);

  const todoItems = useMemo(
    () => toProjectProps(todoLane.data?.pages.flatMap(page => page.items) ?? [], editingProjectIds),
    [todoLane.data, editingProjectIds],
  );
  const inProgressItems = useMemo(
    () =>
      toProjectProps(
        inProgressLane.data?.pages.flatMap(page => page.items) ?? [],
        editingProjectIds,
      ),
    [inProgressLane.data, editingProjectIds],
  );
  const doneItems = useMemo(
    () => toProjectProps(doneLane.data?.pages.flatMap(page => page.items) ?? [], editingProjectIds),
    [doneLane.data, editingProjectIds],
  );

  const lanesByChip: Record<
    StatusChip,
    {
      hasMore: boolean;
      isLoading: boolean;
      isLoadingMore: boolean;
      items: ProjectProps[];
      onReachEnd: () => void;
    }
  > = {
    TODO: {
      items: todoItems,
      isLoading: todoLane.isLoading,
      isLoadingMore: todoLane.isFetchingNextPage,
      hasMore: todoLane.hasNextPage ?? false,
      onReachEnd: () => void todoLane.fetchNextPage(),
    },
    IN_PROGRESS: {
      items: inProgressItems,
      isLoading: inProgressLane.isLoading,
      isLoadingMore: inProgressLane.isFetchingNextPage,
      hasMore: inProgressLane.hasNextPage ?? false,
      onReachEnd: () => void inProgressLane.fetchNextPage(),
    },
    DONE: {
      items: doneItems,
      isLoading: doneLane.isLoading,
      isLoadingMore: doneLane.isFetchingNextPage,
      hasMore: doneLane.hasNextPage ?? false,
      onReachEnd: () => void doneLane.fetchNextPage(),
    },
  };

  const anyLaneLoading = todoLane.isLoading || inProgressLane.isLoading || doneLane.isLoading;
  const projectsError =
    todoLane.isError || inProgressLane.isError || doneLane.isError || counts.isError;

  // "Shown" sums what's actually loaded across the three (already
  // search-scoped) lanes — total is the unfiltered counts sum above, so this
  // reads as "N of M" the same way it did with the old client-side filter,
  // even though N here also happens to track lazy-loading progress once a
  // lane has more pages than are currently in view.
  const shownCount = todoItems.length + inProgressItems.length + doneItems.length;

  const handleSelect = useCallback(
    (projectId: string) => void navigate(`/projects/${projectId}`),
    [navigate],
  );
  const handleStatusChange = useCallback(
    (projectId: string, status: ProjectProps['status']) =>
      handleProjectUpdate(projectId, { status }),
    [handleProjectUpdate],
  );

  if (productLoading) {
    return <ProductViewSkeleton />;
  }

  if (!product) {
    return (
      <div className={`p-6 ${MAIN_CONTENT_WIDTH}`}>
        <p className="text-sm text-error">Product not found</p>
      </div>
    );
  }

  const hasSearchQuery = debouncedSearchQuery.trim().length > 0;
  const noSearchMatches = totalCount > 0 && hasSearchQuery && !anyLaneLoading && shownCount === 0;

  return (
    <div className={`flex h-full flex-col gap-4 overflow-hidden p-6 ${MAIN_CONTENT_WIDTH}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Button
            isIconOnly
            aria-label="Go back"
            size="sm"
            variant="light"
            onPress={() => void navigate(-1)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-primary-700">
              Project board
            </p>
            <h1
              ref={headingRef}
              className="text-xl font-semibold text-foreground focus:outline-none"
              tabIndex={-1}
            >
              {product.name}
            </h1>
            {product.description && (
              <p className="mt-1 text-sm text-muted">{product.description}</p>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            startContent={<GitBranch aria-hidden="true" className="h-4 w-4" />}
            variant="flat"
            onPress={() => setLinkOpen(true)}
          >
            Link Repository
          </Button>
          <Button
            startContent={<Pencil aria-hidden="true" className="h-4 w-4" />}
            variant="flat"
            onPress={() => setEditOpen(true)}
          >
            Edit
          </Button>
        </div>
      </div>

      <FilterBar
        controls={
          <ButtonGroup size="sm">
            <Tooltip content="Newest first">
              <Button
                aria-label="Sort by newest first"
                color={sortOption === 'newest' ? 'primary' : 'default'}
                isIconOnly
                onPress={() => setSortOption('newest')}
              >
                <Clock className="h-4 w-4" />
              </Button>
            </Tooltip>
            <Tooltip content="Oldest first">
              <Button
                aria-label="Sort by oldest first"
                color={sortOption === 'oldest' ? 'primary' : 'default'}
                isIconOnly
                onPress={() => setSortOption('oldest')}
              >
                <SortAsc className="h-4 w-4" />
              </Button>
            </Tooltip>
          </ButtonGroup>
        }
        count={{ shown: shownCount, total: totalCount }}
        searchPlaceholder="Search projects..."
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
      />

      {projectsError && (
        <div className="rounded-md border border-danger/40 bg-danger-100 p-3 text-sm text-danger-700 dark:bg-danger-200 dark:text-danger-800">
          Failed to load projects. Please try again later.
        </div>
      )}

      {noSearchMatches ? (
        <p className="text-sm text-muted">No projects match your search.</p>
      ) : (
        <Board className="flex-1">
          {FILTER_CHIPS.map(chip => {
            const { Icon, iconClass, label } = STATUS_DISPLAY[chip];
            const lane = lanesByChip[chip];
            return (
              <BoardColumn
                key={chip}
                count={counts.data?.[chip]}
                hasMore={lane.hasMore}
                isLoadingMore={lane.isLoadingMore}
                label={label}
                statusIcon={<Icon aria-hidden="true" className={iconClass} />}
                onReachEnd={lane.onReachEnd}
              >
                {lane.isLoading ? (
                  <ColumnSkeleton />
                ) : lane.items.length === 0 ? (
                  <p className="px-1 py-2 text-sm text-muted">No projects</p>
                ) : (
                  lane.items.map(project => (
                    <ProjectCard
                      key={project.id}
                      isSelected={false}
                      project={project}
                      variant="board"
                      onDelete={handleProjectDelete}
                      onGenerateTasksFromPRD={handleGenerateTasksFromPRD}
                      onSelect={handleSelect}
                      onStatusChange={handleStatusChange}
                      onUpdate={handleProjectUpdate}
                    />
                  ))
                )}
              </BoardColumn>
            );
          })}
        </Board>
      )}

      <EditProductModal isOpen={editOpen} product={product} onClose={() => setEditOpen(false)} />
      <LinkReposModal isOpen={linkOpen} productId={product.id} onClose={() => setLinkOpen(false)} />
    </div>
  );
}

/**
 * Adapts a lane's raw `ProjectWithTasks[]` (from `useProductProjectsLane`)
 * into the `ProjectProps[]` shape `ProjectCard` expects. Pure — no React, no
 * I/O — so it can be unit-tested directly without rendering the page.
 *
 * FE-003: replaces `groupByColumn`, which grouped one flat fetched list into
 * three columns client-side — no longer needed now that each column is its
 * own server-scoped lane query and arrives pre-grouped, pre-sorted, and
 * (when searching) pre-filtered.
 *
 * Pattern: docs/design-patterns/common/code-readability.md — small, named helper
 */
export function toProjectProps(
  projects: ProjectWithTasks[],
  editingProjectIds: ReadonlySet<string>,
): ProjectProps[] {
  return projects.map(project => ({
    ...project,
    prdContent: project.prdContent || undefined,
    description: project.description || '',
    isEditing: editingProjectIds.has(project.id),
  }));
}
